import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Title2,
  Body1,
  Spinner,
} from '@fluentui/react-components';
import {
  PersonAddRegular,
  PersonDeleteRegular,
  CheckmarkCircleRegular,
  PeopleTeamRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../config/constants';
import { apiClient } from '../api/client';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  greeting: {
    marginBottom: tokens.spacingVerticalS,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalXXL,
  },
  card: {
    cursor: 'pointer',
    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
    ':hover': {
      boxShadow: tokens.shadow16,
      transform: 'translateY(-2px)',
    },
  },
  cardIcon: {
    width: '48px',
    height: '48px',
    borderRadius: tokens.borderRadiusLarge,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: tokens.spacingVerticalM,
    fontSize: '24px',
  },
  onboardIcon: {
    backgroundColor: '#e6f4ea',
    color: '#137333',
  },
  offboardIcon: {
    backgroundColor: '#fce8e6',
    color: '#c5221f',
  },
  approvalIcon: {
    backgroundColor: '#e8f0fe',
    color: '#1967d2',
  },
  teamsIcon: {
    backgroundColor: '#fef7e0',
    color: '#b06000',
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalXS,
  },
  cardDescription: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  cardContent: {
    padding: tokens.spacingVerticalL,
  },
  cardAction: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorBrandForeground1,
    marginTop: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase200,
  },
  statsSection: {
    marginTop: tokens.spacingVerticalXXL,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  statCard: {
    padding: tokens.spacingVerticalL,
    textAlign: 'center',
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXS,
  },
});

interface ActionCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  iconClass: string;
  path: string;
  adminOnly?: boolean;
}

interface DashboardStats {
  pendingRequests: number;
  completedThisMonth: number;
  activeEmployees: number;
}

export function DashboardPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Fetch stats for admins
  useEffect(() => {
    if (authLoading || !isAdmin) return;

    setStatsLoading(true);
    apiClient
      .get<{ data: { pendingRequests: number; completedThisMonth: number; activeEmployees: number } }>('/dashboard/stats')
      .then((response) => setStats(response.data))
      .catch(console.error)
      .finally(() => setStatsLoading(false));
  }, [isAdmin, authLoading]);

  const actionCards: ActionCard[] = [
    {
      title: 'New Onboarding',
      description: 'Start the onboarding process for a new employee or contractor',
      icon: <PersonAddRegular />,
      iconClass: styles.onboardIcon,
      path: ROUTES.ONBOARDING,
    },
    {
      title: 'Offboarding',
      description: 'Initiate the offboarding process for a departing employee',
      icon: <PersonDeleteRegular />,
      iconClass: styles.offboardIcon,
      path: ROUTES.OFFBOARDING,
    },
    {
      title: 'Pending Approvals',
      description: 'Review and approve pending onboarding and offboarding requests',
      icon: <CheckmarkCircleRegular />,
      iconClass: styles.approvalIcon,
      path: ROUTES.APPROVAL,
      adminOnly: true,
    },
    {
      title: 'Create Teams Site',
      description: 'Set up a new Microsoft Teams site for your team or project',
      icon: <PeopleTeamRegular />,
      iconClass: styles.teamsIcon,
      path: ROUTES.TEAMS,
      adminOnly: true,
    },
  ];

  const visibleCards = actionCards.filter(card => !card.adminOnly || isAdmin);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2 className={styles.greeting}>
          Welcome back, {user?.name?.split(' ')[0] || 'User'}
        </Title2>
        <Body1 className={styles.subtitle}>
          What would you like to do today?
        </Body1>
      </div>

      <div className={styles.grid}>
        {visibleCards.map((card) => (
          <Card
            key={card.path}
            className={styles.card}
            onClick={() => navigate(card.path)}
          >
            <div className={styles.cardContent}>
              <div className={`${styles.cardIcon} ${card.iconClass}`}>
                {card.icon}
              </div>
              <Text className={styles.cardTitle}>{card.title}</Text>
              <Text className={styles.cardDescription}>{card.description}</Text>
              <div className={styles.cardAction}>
                Get started <ArrowRightRegular />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {isAdmin && (
        <div className={styles.statsSection}>
          <Title2 style={{ marginBottom: tokens.spacingVerticalM }}>Quick Stats</Title2>
          <div className={styles.statsGrid}>
            <Card className={styles.statCard}>
              <Text className={styles.statValue}>
                {statsLoading ? <Spinner size="tiny" /> : (stats?.pendingRequests ?? '-')}
              </Text>
              <Text className={styles.statLabel}>Pending Requests</Text>
            </Card>
            <Card className={styles.statCard}>
              <Text className={styles.statValue}>
                {statsLoading ? <Spinner size="tiny" /> : (stats?.completedThisMonth ?? '-')}
              </Text>
              <Text className={styles.statLabel}>Completed This Month</Text>
            </Card>
            <Card className={styles.statCard}>
              <Text className={styles.statValue}>
                {statsLoading ? <Spinner size="tiny" /> : (stats?.activeEmployees ?? '-')}
              </Text>
              <Text className={styles.statLabel}>Active Employees</Text>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
